import { runtimeDiagnosticContext, withRuntimeDiagnosticQuery } from "./runtime-diagnostics";
import { logRtcSfuRecovery, summarizeRtcSfuEndpoint, summarizeRtcSfuError } from "./rtc-sfu-recovery-debug";

const DEFAULT_ALLOCATION_TIMEOUT_MS = 300000;

function trimTrailingSlash(value) {
  return value ? value.replace(/\/+$/, "") : "";
}

export function resolveRtcSfuOrchestratorUrl({
  searchParams = new URLSearchParams(location.search),
  configUrl = "",
  hostname = location.hostname,
  protocol = location.protocol
} = {}) {
  const queryOverride = searchParams.get("rtc_sfu_orchestrator_url");
  if (queryOverride) {
    return trimTrailingSlash(queryOverride);
  }

  if (configUrl) {
    return trimTrailingSlash(configUrl);
  }

  if (hostname.includes("-verse.")) {
    return `${protocol}//rtc-sfu-orchestrator.${hostname}`;
  }

  return "";
}

export function shouldUseRtcSfuOrchestrator({
  searchParams = new URLSearchParams(location.search),
  orchestratorUrl = resolveRtcSfuOrchestratorUrl({ searchParams })
} = {}) {
  if (["0", "false", "off"].includes((searchParams.get("rtc_sfu_orchestrator") || "").toLowerCase())) {
    return false;
  }

  if (searchParams.get("dialog_url") || searchParams.get("dialog_host") || searchParams.get("dialog_port")) {
    return false;
  }

  return Boolean(orchestratorUrl);
}

function shouldPreserveHubTurn(allocation) {
  return allocation?.allocationId === "shared";
}

export function allocationToDialogEndpoint(allocation, fallbackTurn = null) {
  const signalingUrl = allocation?.signalingUrl;
  if (!signalingUrl) {
    throw new Error("RTC SFU allocation did not include a signaling URL.");
  }

  const url = new URL(signalingUrl);
  const iceServers = allocation.iceServers || allocation.ice_servers;

  return {
    host: allocation.signalingHost || url.hostname,
    port: allocation.signalingPort || url.port || (url.protocol === "wss:" ? "443" : "80"),
    turn: shouldPreserveHubTurn(allocation) ? fallbackTurn : null,
    ...(iceServers ? { iceServers } : {}),
    serverUrl: signalingUrl,
    usingOverride: true,
    usingRtcSfuOrchestrator: true,
    allocation
  };
}

async function readJSONResponse(response) {
  const text = await response.text();
  if (!text) {
    return null;
  }

  return JSON.parse(text);
}

async function requestJSON(url, { method = "GET", headers = {}, body, fetchImpl = fetch } = {}) {
  let response;

  try {
    response = await fetchImpl(url, {
      method,
      headers: {
        Accept: "application/json",
        ...headers
      },
      body: body === undefined ? undefined : JSON.stringify(body)
    });
  } catch (err) {
    logRtcSfuRecovery("orchestrator-http:fetch-error", {
      method,
      url,
      error: summarizeRtcSfuError(err)
    });
    throw err;
  }

  const payload = await readJSONResponse(response);

  if (!response.ok) {
    const message = payload?.error?.message || payload?.message || response.statusText || "Request failed.";
    const error = new Error(message);
    error.name = "RtcSfuRequestError";
    error.status = response.status;
    error.statusText = response.statusText;
    error.payload = payload;
    error.url = url;
    logRtcSfuRecovery("orchestrator-http:error", {
      method,
      url,
      status: response.status,
      statusText: response.statusText,
      message,
      payloadState: payload?.state
    });
    throw error;
  }

  return { response, payload };
}

export async function requestRtcSfuAllocation({
  roomId,
  hubId = roomId,
  clientId,
  allocationGrant,
  requestAllocationGrant,
  dashboardApiUrl,
  orchestratorUrl = resolveRtcSfuOrchestratorUrl(),
  fetchImpl = fetch,
  timeoutMs = DEFAULT_ALLOCATION_TIMEOUT_MS,
  now = Date.now,
  sleepImpl = delay => new Promise(resolve => setTimeout(resolve, delay)),
  onStateChange = () => {}
}) {
  logRtcSfuRecovery("allocation:request:start", {
    roomId,
    hubId,
    clientId,
    orchestratorUrl,
    dashboardApiUrl,
    hasAllocationGrant: Boolean(allocationGrant)
  });

  if (!orchestratorUrl) {
    throw new Error("RTC SFU orchestrator URL is not configured.");
  }

  if (!allocationGrant) {
    if (!requestAllocationGrant) {
      throw new Error("Platform Room allocation grant endpoint is not available.");
    }
    allocationGrant = (await requestAllocationGrant(roomId))?.allocationGrant;
    if (!allocationGrant) {
      throw new Error("Platform Room did not return an RTC SFU allocation grant.");
    }
    logRtcSfuRecovery("allocation-grant:response", { roomId, hasAllocationGrant: Boolean(allocationGrant) });

    if (!allocationGrant) {
      throw new Error("Dashboard did not return an RTC SFU allocation grant.");
    }
  }

  const allocationUrl = `${trimTrailingSlash(orchestratorUrl)}/api/rtc-sfu/rooms/${encodeURIComponent(
    roomId
  )}/allocation`;
  const startedAt = now();
  const diagnosticContext = runtimeDiagnosticContext(roomId);
  const correlatedAllocationUrl = withRuntimeDiagnosticQuery(allocationUrl, diagnosticContext);
  let current = await requestJSON(correlatedAllocationUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${allocationGrant}`
    },
    body: { clientId, hubId, allocationGrant },
    fetchImpl
  });
  logRtcSfuRecovery("allocation:response", {
    roomId,
    status: current.response.status,
    state: current.payload?.state,
    allocationId: current.payload?.allocationId,
    signalingUrl: current.payload?.signalingUrl
  });

  while (current.response.status === 202 || current.payload?.state === "allocating") {
    onStateChange("provisioning");

    if (now() - startedAt > timeoutMs) {
      throw new Error("RTC SFU allocation timed out.");
    }

    const retryAfterMs = current.payload?.retryAfterMs || 2000;
    logRtcSfuRecovery("allocation:poll-wait", {
      roomId,
      state: current.payload?.state,
      retryAfterMs,
      elapsedMs: now() - startedAt
    });
    await sleepImpl(retryAfterMs);
    const statusUrl = current.payload?.statusUrl
      ? new URL(current.payload.statusUrl, trimTrailingSlash(orchestratorUrl)).toString()
      : allocationUrl;

    logRtcSfuRecovery("allocation:poll-request", { roomId, statusUrl });
    current = await requestJSON(withRuntimeDiagnosticQuery(statusUrl, diagnosticContext), {
      headers: {
        Authorization: `Bearer ${allocationGrant}`
      },
      fetchImpl
    });
    logRtcSfuRecovery("allocation:poll-response", {
      roomId,
      status: current.response.status,
      state: current.payload?.state,
      allocationId: current.payload?.allocationId,
      signalingUrl: current.payload?.signalingUrl
    });
  }

  logRtcSfuRecovery("allocation:done", {
    roomId,
    allocationId: current.payload?.allocationId,
    signalingUrl: current.payload?.signalingUrl
  });

  return current.payload;
}

export async function resolveRtcSfuDialogEndpoint({
  hub,
  clientId,
  allocationGrant,
  requestAllocationGrant,
  searchParams = new URLSearchParams(location.search),
  fetchImpl = fetch,
  dashboardApiUrl,
  orchestratorUrl = resolveRtcSfuOrchestratorUrl({ searchParams }),
  onStateChange
}) {
  const allocation = await requestRtcSfuAllocation({
    roomId: hub.hub_id,
    hubId: hub.hub_id,
    clientId,
    allocationGrant,
    requestAllocationGrant,
    dashboardApiUrl,
    orchestratorUrl,
    fetchImpl,
    onStateChange
  });

  const endpoint = allocationToDialogEndpoint(allocation, hub.turn);
  logRtcSfuRecovery("allocation:endpoint", { endpoint: summarizeRtcSfuEndpoint(endpoint) });

  return endpoint;
}
